import React, { useEffect, useState } from 'react'
import { Drawer, Table, TablePaginationConfig, Typography } from 'antd'
import type { ColumnType } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import styled from 'styled-components'
import { useGetTaskDetail, useGetTaskDetailTemplateList } from './utils'
import { SignTitle } from '@/components'
import moment from 'moment'
import CaseModal from './case-modal'
import { t } from '@/i18n'

interface PropsType {
  open: boolean
  onClose: () => void
  id: string
}

export enum Baswafv1Encode {
  EncodeDynamic = 'EncodeDynamic',
  EncodeStatic = 'EncodeStatic',
  EncodeUnknown = 'EncodeUnknown',
}

export enum Baswafv1TaskKindDetail {
  TaskKindDetailBooking = 'TaskKindDetailBooking',
  TaskKindDetailCycleDay = 'TaskKindDetailCycleDay',
  TaskKindDetailCycleMonth = 'TaskKindDetailCycleMonth',
  TaskKindDetailCycleWeek = 'TaskKindDetailCycleWeek',
  TaskKindDetailImmediately = 'TaskKindDetailImmediately',
  TaskKindDetailUnknown = 'TaskKindDetailUnknown',
}

export interface V1TaskDeviceInfo {
  deviceIp?: string
  deviceName?: string
  deviceType?: string
  isOnline?: boolean
}

export interface TaskVerificationInfo {
  assetName?: string
  attackSource?: string
  attackTarget?: string
  bizAreaName?: string
  devices?: V1TaskDeviceInfo[]
  verificationMode?: string
}

export interface DetailPropsType {
  areaName?: string
  createTime?: string
  cycleDay?: string[]
  cycleTime?: string
  db?: string
  encode?: Baswafv1Encode
  enctypeTypes?: string[]
  groupName?: string
  language?: string
  os?: string
  poolName?: string
  priority?: number
  reduceSpeed?: number
  site?: string
  taskId?: string
  taskKind?: Baswafv1TaskKindDetail
  taskName?: string
  templateNames?: string[]
  verificationInfo?: TaskVerificationInfo
  wafName?: string
}

interface DetailDisplayType extends Omit<DetailPropsType, 'taskKind'> {
  taskKind?: string
}

interface TemplateInfo {
  name: string
  resourceAmount: number
  updateTime: string
  templateId: string
}

interface TemplateResponse {
  list: TemplateInfo[]
  total: number
}

enum SortOrder {
  SORT_ORDER_UNSPECIFIED = 'SORT_ORDER_UNSPECIFIED',
  SORT_ORDER_ASCENDING = 'SORT_ORDER_ASCENDING',
  SORT_ORDER_DESCENDING = 'SORT_ORDER_DESCENDING',
}

const KindMap = {
  TaskKindDetailImmediately: t('立即任务'),
  TaskKindDetailBooking: t('预定任务'),
  TaskKindDetailCycleDay: t('周期任务 每日'),
  TaskKindDetailCycleWeek: t('周期任务 每周'),
  TaskKindDetailCycleMonth: t('周期任务 每月'),
  TaskKindDetailUnknown: t('未知'),
  TaskKindDetailHighFrequencyMinutely: t('高频 每分钟'),
  TaskKindDetailHighFrequencyHourly: t('高频 每小时'),
}

const EncodeMap = {
  EncodeDynamic: t('动态'),
  EncodeStatic: t('静态'),
  EncodeUnknown: t('未知'),
}

const WeekMap = {
  1: t('周一'),
  2: t('周二'),
  3: t('周三'),
  4: t('周四'),
  5: t('周五'),
  6: t('周六'),
  0: t('周日'),
}

const DetailDrawer: React.FC<PropsType> = ({ open, onClose, id }) => {
  const [getTaskDetail] = useGetTaskDetail()
  const [getTaskDetailTemplateList, loading] = useGetTaskDetailTemplateList()
  const [taskDetail, setTaskDetail] = useState<DetailDisplayType>()
  const [templateData, setTemplateData] = useState<TemplateResponse>({
    list: [],
    total: 0,
  })
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    sortOrder: SortOrder.SORT_ORDER_UNSPECIFIED,
    taskId: id,
  })
  const [caseModalVisible, setCaseModalVisible] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')

  const buildDetailData = (data: DetailPropsType): DetailDisplayType => {
    const detailObj = { ...data }
    if (detailObj.taskKind) {
      const displayTaskKind = KindMap[detailObj.taskKind] || KindMap.TaskKindDetailUnknown
      return {
        ...detailObj,
        taskKind: displayTaskKind,
      }
    }
    return detailObj
  }

  useEffect(() => {
    if (!open)
      return setSearchParams({
        page: 1,
        pageSize: 10,
        sortOrder: SortOrder.SORT_ORDER_UNSPECIFIED,
        taskId: '',
      })
    getTaskDetail(id)
      .then(res => {
        setTaskDetail(buildDetailData(res.data))
      })
      .catch(_ => {})
  }, [open])

  useEffect(() => {
    if (!open) return
    if (!id) return
    getTaskDetailTemplateList({ ...searchParams, taskId: id })
      .then(res => {
        setTemplateData({
          list: res.data.list || [],
          total: res.data.total || 0,
        })
      })
      .catch(_ => {})
  }, [open, searchParams, id])

  const isWeeklyTask = taskDetail?.taskKind?.includes(t('周期任务 每周'))

  const templateColumns: ColumnType<TemplateInfo>[] = [
    {
      title: t('模板名称'),
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      ellipsis: true,
    },
    {
      title: t('用例数量'),
      dataIndex: 'resourceAmount',
      key: 'resourceAmount',
      width: '30%',
      render: (count: number, record: TemplateInfo) => (
        <button
          type="button"
          className="usage-count-btn"
          onClick={() => {
            setSelectedTemplate(record.templateId)
            setCaseModalVisible(true)
          }}
        >
          {count}
        </button>
      ),
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '35%',
      sorter: true,
      sortOrder:
        searchParams.sortOrder === SortOrder.SORT_ORDER_UNSPECIFIED
          ? undefined
          : searchParams.sortOrder === SortOrder.SORT_ORDER_ASCENDING
          ? 'ascend'
          : 'descend',
      render: (time: string) => (
        <span>{time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<TemplateInfo> | SorterResult<TemplateInfo>[],
  ) => {
    console.log('sorter', sorter, pagination)
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortOrder: Array.isArray(sorter)
        ? sorter[0]?.order === 'ascend'
          ? SortOrder.SORT_ORDER_ASCENDING
          : sorter[0]?.order === 'descend'
          ? SortOrder.SORT_ORDER_DESCENDING
          : SortOrder.SORT_ORDER_UNSPECIFIED
        : sorter.order === 'ascend'
        ? SortOrder.SORT_ORDER_ASCENDING
        : sorter.order === 'descend'
        ? SortOrder.SORT_ORDER_DESCENDING
        : SortOrder.SORT_ORDER_UNSPECIFIED,
    }))
  }

  return (
    <>
      <DrawerWrapper
        title={<SignTitle show>{t('任务详情')}</SignTitle>}
        width={720}
        open={open}
        onClose={onClose}
        footer={null}
        zIndex={1000}
      >
        <div>
          <div className="task-config">
            <div className="title">
              <span>{t('任务配置信息')}</span>
            </div>
            <div className="content">
              <div className="item">
                <span className="label">{t('任务名称：')}</span>
                <span className="value">{taskDetail?.taskName}</span>
              </div>
              <div className="item">
                <div className="label">{t('任务分组：')}</div>
                <div className="value">{taskDetail?.groupName || '-'}</div>
              </div>
              <div className="verification-item">
                <div className="verification-label">{t('验证模式：')}</div>
                <div className="verification-content">
                  <div className="config-name">
                    <span className="config-label">{t('配置名称：')}</span>
                    {taskDetail?.verificationInfo?.attackSource &&
                    taskDetail?.verificationInfo?.attackTarget ? (
                      <div className="config-value">
                        <span>{taskDetail?.verificationInfo?.attackSource}</span>
                        <span className="arrow">→</span>
                        <span>{taskDetail?.verificationInfo?.attackTarget}</span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className="asset-info">
                    <div className="asset-item">
                      <span className="asset-label">{t('资产名称：')}</span>
                      <span className="asset-value">
                        {taskDetail?.verificationInfo?.assetName || '-'}
                      </span>
                    </div>
                    <div className="asset-item">
                      <span className="asset-label">{t('安全域：')}</span>
                      <span className="asset-value">
                        {taskDetail?.verificationInfo?.bizAreaName || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="item">
                <div className="label">{t('执行类型：')}</div>
                <div className="value">
                  {taskDetail?.taskKind?.includes(t('高频 每分钟')) ? (
                    `高频 每${taskDetail?.cycleDay?.[0]}分钟`
                  ) : taskDetail?.taskKind?.includes(t('高频 每小时')) ? (
                    `高频 每${taskDetail?.cycleDay?.[0]}小时`
                  ) : (
                    <>
                      <span>{taskDetail?.taskKind}</span>
                      <span style={{ marginLeft: '10px' }}>
                        {taskDetail?.cycleDay?.map(day => (
                          <span
                            style={{
                              marginRight: '5px',
                              marginBottom: '5px',
                              display: 'inline-block',
                            }}
                            key={day}
                          >
                            {isWeeklyTask ? WeekMap[day] : `${day}号`}{' '}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                  <span style={{ marginLeft: '5px' }}>{taskDetail?.cycleTime}</span>
                </div>
              </div>
              <div className="item">
                <div className="label">{t('执行速率：')}</div>
                <div className="value">
                  {taskDetail?.reduceSpeed} {t('个/秒')}
                </div>
              </div>
              <div className="item">
                <div className="label">{t('任务优先级：')}</div>
                <div className="value">{taskDetail?.priority || '3'}</div>
              </div>
              <div className="item">
                <div className="label">{t('代理池：')}</div>
                <div className="value">{taskDetail?.poolName || '-'}</div>
              </div>
              <div className="item">
                <div className="label">{t('创建时间：')}</div>
                <div className="value">{taskDetail?.createTime}</div>
              </div>
              <div className="item">
                <div className="label">{t('动态编码：')}</div>
                <div className="value">
                  {taskDetail?.encode === 'EncodeDynamic' ? t('是') : t('否')}
                </div>
              </div>
              {taskDetail?.encode === 'EncodeDynamic' && (
                <div className="item">
                  <div className="label">{t('编码类型：')}</div>
                  <div className="value">
                    {(taskDetail?.enctypeTypes || []).length > 0
                      ? taskDetail?.enctypeTypes?.map((type, index) => (
                          <span key={index} className="encode-tag">
                            {type}
                          </span>
                        ))
                      : '-'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {Boolean(taskDetail?.verificationInfo?.devices?.length) && (
            <div className="device-info">
              <div className="title">
                <span>{t('关联安全设备')}</span>
              </div>
              <div className="device-list">
                {taskDetail?.verificationInfo?.devices?.map((device, index) => (
                  <div key={index} className="device-item">
                    <div className="device-field">
                      <span className="field-label">{t('设备类型：')}</span>
                      <span className="field-value">{device.deviceType || 'WAF'}</span>
                    </div>
                    <div className="device-field">
                      <span className="field-label">{t('设备名称：')}</span>
                      <span className="field-value">{device.deviceName || t('设备名称')}</span>
                    </div>
                    <div className="device-field">
                      <span className="field-label">{t('设备IP：')}</span>
                      <span className="field-value">{device.deviceIp}</span>
                    </div>
                    <div className="device-field">
                      <span
                        className="status-dot"
                        data-status={device.isOnline ? 'online' : 'offline'}
                      />

                      <span className="status-text">{device.isOnline ? t('在线') : t('离线')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="template-info">
            <div className="title">
              <span>{t('模板信息')}</span>
            </div>
            <Table
              className="template-table"
              columns={templateColumns}
              dataSource={templateData.list}
              showSorterTooltip={false}
              loading={loading}
              pagination={{
                current: searchParams.page,
                total: templateData.total,
                pageSize: searchParams.pageSize,
                showSizeChanger: true,
                size: 'small',
                showTotal: total => `${t('共')} ${total} ${t('条')}`,
              }}
              onChange={handleTableChange}
              size="small"
              rowKey="templateId"
            />
          </div>
        </div>
        <CaseModal
          open={caseModalVisible}
          onClose={() => {
            setCaseModalVisible(false)
          }}
          taskId={id}
          templateId={selectedTemplate}
        />
      </DrawerWrapper>
    </>
  )
}

export default DetailDrawer

const DrawerWrapper = styled(Drawer)`
  .ant-drawer-close {
    width: 36px;
    height: 36px;
    background: #d8d6f4;
    box-shadow: -1px 4px 10px 0px #c7c7c7;
    border-radius: 6px;
    border: none;
    margin-right: 0px;
    color: #4238c9;
    font-size: 16px;
    font-weight: 500;
    position: absolute;
    right: 0px;
    top: 0px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translate(-10px, 8px);
      box-shadow: 0 0 0 0;
    }

    .ant-drawer-close-x {
      width: 100%;
      height: 100%;
      line-height: 36px;
      text-align: center;
    }
  }

  .ant-drawer-header {
    height: 56px;
    background: #f2f0ff;
    border-bottom: none;
    margin: 0;
  }

  .task-config,
  .device-info,
  .template-info {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    .title {
      font-size: 14px;
      font-weight: 500;
      color: #1d2129;
      margin-bottom: 16px;
      position: relative;
      /* padding-left: 10px; */

      /* &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 2px;
        height: 14px;
        background: #165dff;
      } */
    }

    .content {
      display: flex;
      flex-direction: column;

      .item {
        margin-bottom: 16px;
        display: flex;
        align-items: flex-start;

        .label {
          min-width: 70px;
          color: #86909c;
          margin-right: 8px;
          line-height: 20px;
          flex-shrink: 0;
        }

        .value {
          color: #1d2129;
          line-height: 20px;
          flex: 1;
        }
      }
    }
  }

  .ant-table-column-sorters {
    justify-content: flex-start;
    .ant-table-column-sorter {
      padding-top: 2px;
      margin-left: 5px;
    }
    .ant-table-column-title {
      flex: none;
    }
  }

  .device-list {
    .device-item {
      padding: 12px 16px;
      background: #f7f8fa;
      border-radius: 2px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:last-child {
        margin-bottom: 0;
      }

      .device-field {
        display: flex;
        align-items: center;
        // margin-bottom: 8px;
        color: #4e5969;
        font-size: 13px;
        line-height: 20px;

        // &:last-child {
        //   margin-bottom: 0;
        // }

        .field-label {
          color: #86909c;
          margin-right: 8px;
          min-width: 70px;
        }

        .field-value {
          color: #4e5969;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 4px;

          &[data-status='online'] {
            background: #00b42a;
          }

          &[data-status='offline'] {
            background: #86909c;
          }
        }

        .status-text {
          color: #4e5969;
        }
      }
    }
  }

  .template-table {
    .ant-table {
      background: #f7f8fa;
      border-radius: 2px;
    }

    .ant-table-thead > tr > th {
      background: #f2f3f5;
      color: #1d2129;
      font-weight: 500;
      padding: 12px 16px;
      border-bottom: 1px solid #e5e6eb;
    }

    .ant-table-tbody > tr > td {
      background: #f7f8fa;
      border-bottom: 1px solid #e5e6eb;
      padding: 12px 16px;
    }

    .template-name {
      color: #1d2129;
    }

    .usage-count-btn {
      color: #4238c9;
      border: none;
      background: none;
      padding: 0;
      cursor: pointer;
      font-size: inherit;
    }

    .update-time {
      color: #86909c;
    }

    .ant-pagination {
      margin: 16px 0 0;
      text-align: right;

      .ant-pagination-total-text {
        color: #86909c;
      }
    }
  }

  .encode-tag {
    display: inline-block;
    padding: 1px 8px;
    background: #f2f3f5;
    border-radius: 2px;
    font-size: 12px;
    color: #4e5969;
    margin-right: 8px;
    margin-bottom: 8px;
    line-height: 20px;

    &:last-child {
      margin-right: 0;
    }
  }

  .verification-item {
    margin-bottom: 16px;

    .verification-label {
      color: #86909c;
      font-size: 13px;
      line-height: 20px;
      margin-bottom: 8px;
    }

    .verification-content {
      background: #f7f8fa;
      border-radius: 2px;
      padding: 12px;

      .config-name {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        font-size: 13px;
        line-height: 20px;

        .config-label {
          color: #86909c;
          margin-right: 8px;
          min-width: 70px;
          flex-shrink: 0;
        }

        .config-value {
          display: flex;
          align-items: center;
          color: #1d2129;
          flex: 1;

          .arrow {
            color: #4238c9;
            margin: 0 8px;
            font-family: SimSun, sans-serif;
          }
        }
      }

      .asset-info {
        .asset-item {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          font-size: 13px;
          line-height: 20px;

          &:last-child {
            margin-bottom: 0;
          }

          .asset-label {
            color: #86909c;
            margin-right: 8px;
            min-width: 70px;
            flex-shrink: 0;
          }

          .asset-value {
            color: #4e5969;
            flex: 1;
          }
        }
      }
    }
  }
`
